import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { APP_GROUPS } from "./app-catalog";
import { useAppNav } from "./app-nav";
import { useAuth } from "./auth";
import { api } from "@/lib/api";
import { inboxArchive } from "./inbox-api";
import { t, locale } from "@/lib/i18n";
import HelpBlock from "./help";

/** Счётчики на плитках (замечание №4): ключ — type плитки, значение — число из GET /search/counts. */
type HubCounts = Partial<Record<string, number>>;

/** Главный экран рабочего пространства: все разделы плиткой, без длинной ленты. */
export default function AppHub() {
  const { open } = useAppNav();
  const { user } = useAuth();
  const [query, setQuery] = useState("");
  const [counts, setCounts] = useState<HubCounts>({});
  useEffect(() => {
    if (!user) return;
    api<HubCounts>(`/search/counts${inboxArchive(user.role) ? "?archive=true" : ""}`).then(setCounts, () => setCounts({}));
  }, [user]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return APP_GROUPS;
    return APP_GROUPS.map((g) => ({
      ...g,
      items: g.items.filter((i) => `${i.label} ${i.hint}`.toLowerCase().includes(q)),
    })).filter((g) => g.items.length > 0);
  }, [query]);

  return (
    <main id="main" className="mx-auto w-full max-w-[1440px] px-4 pb-10 pt-4 md:px-6 md:pt-5">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight md:text-2xl">{t("Все разделы")}</h1>
          <p className="mt-1 text-xs text-muted-foreground md:text-sm">
            {t("Всё рабочее пространство на одном экране — выберите раздел, он откроется целиком.")}
          </p>
        </div>
        <div className="flex h-10 w-full items-center gap-2 rounded-btn border border-border bg-card px-3 sm:w-72">
          <Search className="size-4 flex-none text-muted-foreground" aria-hidden />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("Найти раздел")}
            aria-label={t("Найти раздел")}
            className="h-full w-full min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="rounded-btn border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
          {t("Ничего не нашлось — попробуйте другое слово.")}
        </p>
      ) : (
        groups.map((group) => (
          <section key={group.title} className="mb-3 last:mb-0">
            <h2 className="mb-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-[.16em] text-muted-foreground">{group.title}</h2>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {group.items.map((item) => (
                <button
                  key={item.type}
                  type="button"
                  onClick={() => open(item.type)}
                  className="group flex min-w-0 cursor-pointer items-center gap-2.5 rounded-btn border border-border bg-card px-2.5 py-2 text-left transition hover:border-primary/60 hover:bg-background-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                    <item.icon className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <b className="block truncate text-[13px] font-semibold leading-tight text-foreground">{item.label}</b>
                    <span className="block truncate text-[11px] text-muted-foreground">{item.hint}</span>
                  </span>
                  {counts[item.type] !== undefined ? (
                    <span
                      className="ms-auto flex-none rounded-full bg-primary/10 px-1.5 py-0.5 text-[10.5px] font-semibold tabular-nums text-primary"
                      title={t("Сколько сейчас в разделе")}
                      data-testid={`tile-count-${item.type}`}
                    >
                      {(counts[item.type] ?? 0).toLocaleString(locale)}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </section>
        ))
      )}
      {!query.trim() && <HelpBlock />}
    </main>
  );
}